import { useState } from "react";
import { Plus } from "lucide-react";
import { Controller, useForm } from "react-hook-form";
import {
  Page,
  Table,
  type Column,
  Alert,
  Badge,
  Button,
  Checkbox,
  Combobox,
  Switch,
  FormModal,
  FormStack,
  Input,
  RelativeDateField,
  Text,
  Tooltip,
  EmptyState,
} from "@marekl/adminer/components";
import { useConfirm } from "@marekl/adminer/hooks";
import { apiErrorToFormErrors, rootErrorMessage } from "@marekl/adminer/forms";
import { notifySuccess } from "@marekl/adminer/api";
import { dayjs } from "@marekl/adminer/datetime";
import { formatDateTime, formatNumber } from "@marekl/adminer/format";
import { useIsAdmin } from "../../auth/useAuth";
import { useSenderList, type Sender } from "../../lib/queries/senders";
import {
  useCreateWorkerToken,
  useDeleteWorkerToken,
  useRevokeWorkerToken,
  useWorkerInstances,
  useWorkerTokenList,
  type WorkerInstance,
  type WorkerToken,
} from "../../lib/queries/workerTokens";
import { TokenRevealModal } from "../../components/TokenRevealModal";
import { RefreshButton } from "../../components/RefreshButton";
import { useNow } from "../../lib/useNow";
import styles from "./WorkersPage.module.css";

const CHANNEL_LABELS: Record<string, string> = {
  email: "Email",
  sms: "SMS",
  push: "Push",
};

/** A worker is considered live when it polled within the last two minutes. */
const LIVENESS_WINDOW_MINUTES = 2;

interface WorkerFormValues {
  name: string;
  email: boolean;
  sms: boolean;
  push: boolean;
  sender_keys: string[];
}

function CreateWorkerModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (token: string, name: string, sms: boolean) => void;
}) {
  const create = useCreateWorkerToken();
  const senders = useSenderList();
  const {
    register,
    control,
    handleSubmit,
    setError,
    watch,
    formState: { errors },
  } = useForm<WorkerFormValues>({
    mode: "onTouched",
    defaultValues: { name: "", email: true, sms: false, push: false, sender_keys: [] },
  });

  const anyChannel = watch("email") || watch("sms") || watch("push");

  // Only senders on a channel this token is being given: offering the rest
  // invites a scope that can never match anything, which looks like a broken
  // worker rather than a mis-scoped token. A dummy sender is left out for
  // the same reason: the server reports its messages sent, no worker ever
  // gets one.
  const chosenChannels = [
    ...(watch("email") ? ["email"] : []),
    ...(watch("sms") ? ["sms"] : []),
    ...(watch("push") ? ["push"] : []),
  ];
  const selectable = (senders.data ?? []).filter(
    (sender) =>
      chosenChannels.includes(sender.channel) && sender.type !== "dummy",
  );

  async function onSubmit(values: WorkerFormValues) {
    const channels = [
      ...(values.email ? (["email"] as const) : []),
      ...(values.sms ? (["sms"] as const) : []),
      ...(values.push ? (["push"] as const) : []),
    ];
    try {
      const created = await create.mutateAsync({
        name: values.name.trim(),
        channels,
        sender_keys: values.sender_keys,
      });
      notifySuccess("The worker token was created.");
      onCreated(created.token, values.name.trim(), values.sms);
    } catch (err) {
      apiErrorToFormErrors<WorkerFormValues>(err, setError, {
        fallbackMessage: "Creation failed. Please try again.",
      });
    }
  }

  return (
    <FormModal
      open
      title="New worker token"
      onClose={onClose}
      busy={create.isPending}
      canSubmit={anyChannel}
      error={rootErrorMessage(errors)}
      submitLabel="Create"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Input
        label="Name"
        required
        placeholder="e.g. smtp-worker-1"
        error={errors.name?.message}
        {...register("name", { required: "Required field" })}
      />
      <FormStack>
        <Checkbox label="Email" {...register("email")} />
        <Checkbox label="SMS" {...register("sms")} />
        <Checkbox label="Push" {...register("push")} />

        <Controller
          name="sender_keys"
          control={control}
          render={({ field }) => (
            <Combobox<Sender, string>
              label="Senders"
              hint={
                "Empty = every sender on the channels above. Scope a worker that " +
                "should only carry its own: an unscoped SMS token claims another " +
                "phone's messages as readily as its own."
              }
              options={selectable}
              getOptionLabel={(sender) => `${sender.name} (${sender.key})`}
              getOptionValue={(sender) => sender.key}
              getOptionKey={(sender) => sender.id}
              multiple
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </FormStack>
      {!anyChannel && (
        <Alert variant="warn">Select at least one channel.</Alert>
      )}
    </FormModal>
  );
}

type ModalState =
  | { kind: "closed" }
  | { kind: "create" }
  | { kind: "reveal"; token: string; name: string; sms: boolean };

/** Everything this page shows, for the header's refresh button. */
const REFRESH_KEYS = [["worker-tokens"], ["worker-instances"]];

export function WorkersPage() {
  const isAdmin = useIsAdmin();
  // A token an admin has tidied away. A revoked one stays in the list — that
  // is recent news — and only what was explicitly deleted hides.
  const [showDeleted, setShowDeleted] = useState(false);
  const { data, isLoading, error, refetch } = useWorkerTokenList(showDeleted);
  const deletedCount = (data ?? []).filter((t) => t.deleted).length;
  const instances = useWorkerInstances();
  // Liveness below is wall-clock, not a server field, so without a tick a
  // token's last poll would keep it Online until the next 30 s refetch
  // happened to re-render the row. The tick also ages the relative dates.
  const now = useNow(15_000);
  const [modal, setModal] = useState<ModalState>({ kind: "closed" });
  const confirm = useConfirm();
  const revoke = useRevokeWorkerToken();
  const remove = useDeleteWorkerToken();

  function confirmDelete(worker: WorkerToken) {
    void confirm({
      title: "Delete worker token",
      description: (
        <>
          Hide <strong>{worker.name}</strong> from this list? The token is kept and is
          already revoked, so it can never be used again — turn on “Show deleted” to
          find it. The phones recorded under it are removed; that is only the record of
          when each last polled.
        </>
      ),
      confirmLabel: "Delete",
      variant: "danger",
      onConfirm: async () => {
        await remove.mutateAsync({ id: worker.id });
      },
    }).then((confirmed) => {
      if (confirmed) notifySuccess("The worker token was hidden.");
    });
  }

  function confirmRevoke(worker: WorkerToken) {
    void confirm({
      title: "Revoke worker token",
      description: (
        <>
          Really revoke the token <strong>{worker.name}</strong>? The worker
          using it will be disconnected immediately.
        </>
      ),
      confirmLabel: "Revoke",
      variant: "danger",
      onConfirm: async () => {
        await revoke.mutateAsync({ id: worker.id });
      },
    }).then((confirmed) => {
      if (confirmed) notifySuccess("The worker token was revoked.");
    });
  }

  const instanceColumns: Column<WorkerInstance>[] = [
    {
      key: "instance",
      header: "Instance",
      primary: true,
      render: (row) => <Text variant="mono">{row.instance}</Text>,
    },
    {
      key: "token",
      header: "Token",
      render: (row) => row.worker_token_name,
    },
    {
      key: "number",
      header: "Sends from",
      // An SMS worker reports the number its SIM sends from; a sender pinned
      // to a number is only served by the phone reporting it. "operator" is a
      // person's answer and beats "sim", which Android often cannot read.
      render: (row) =>
        row.reported_number ? (
          <Text variant="mono">
            {row.reported_number}
            {row.reported_number_source === "sim" && " (from SIM)"}
          </Text>
        ) : (
          <Text variant="muted">—</Text>
        ),
    },
    {
      key: "version",
      header: "Version",
      render: (row) =>
        row.version ? (
          <Text variant="mono">{row.version}</Text>
        ) : (
          <Text variant="muted">—</Text>
        ),
    },
    {
      key: "status",
      header: "Status",
      render: (row) =>
        row.online ? (
          <Badge weight="ghost" variant="ok" dot>Online</Badge>
        ) : (
          <Badge weight="ghost" variant="warn" dot>Offline</Badge>
        ),
    },
    {
      key: "last_seen",
      header: "Last seen",
      render: (row) => <RelativeDateField record={row} source="last_seen_at" />,
    },
    {
      key: "in_flight",
      header: "In flight",
      numeric: true,
      render: (row) => <Text variant="number">{formatNumber(row.in_flight)}</Text>,
    },
    {
      key: "sent",
      header: "Sent 1 h",
      numeric: true,
      render: (row) => <Text variant="number">{formatNumber(row.sent_last_hour)}</Text>,
    },
    {
      key: "failed",
      header: "Failed 1 h",
      numeric: true,
      render: (row) =>
        row.failed_last_hour > 0 ? (
          <Badge variant="err">{formatNumber(row.failed_last_hour)}</Badge>
        ) : (
          <Text variant="number">0</Text>
        ),
    },
    {
      key: "last_error",
      header: "Last error",
      render: (row) =>
        row.last_error ? (
          <Tooltip
            content={
              row.last_error_at
                ? `${formatDateTime(row.last_error_at)} — ${row.last_error}`
                : row.last_error
            }
          >
            <span className={styles.truncate}>
              <Text variant="muted">{row.last_error}</Text>
            </span>
          </Tooltip>
        ) : (
          <Text variant="muted">—</Text>
        ),
    },
  ];

  const columns: Column<WorkerToken>[] = [
    {
      key: "name",
      header: "Name",
      primary: true,
      render: (row) => row.name,
    },
    {
      key: "channels",
      header: "Channels",
      render: (row) => (
        <>
          {row.channels.map((c) => (
            <Badge key={c} variant="info">
              {CHANNEL_LABELS[c] ?? c}
            </Badge>
          ))}
        </>
      ),
    },
    {
      key: "senders",
      header: "Senders",
      render: (row) =>
        row.sender_keys.length ? (
          <Text variant="mono">{row.sender_keys.join(", ")}</Text>
        ) : (
          <Text variant="muted">all</Text>
        ),
    },
    {
      key: "last_seen",
      header: "Last active",
      render: (row) =>
        row.last_seen_at ? (
          <RelativeDateField record={row} source="last_seen_at" />
        ) : (
          <Text variant="muted">never</Text>
        ),
    },
    {
      key: "liveness",
      header: "Status",
      render: (row) => {
        if (!row.active) {
          return (
            <Badge weight="ghost" variant="muted" dot>
              {row.deleted ? "Deleted" : "Revoked"}
            </Badge>
          );
        }
        const live =
          row.last_seen_at &&
          dayjs(now).diff(row.last_seen_at, "minute") < LIVENESS_WINDOW_MINUTES;
        return live ? (
          <Badge weight="ghost" variant="ok" dot>Online</Badge>
        ) : (
          <Badge weight="ghost" variant="warn" dot>Offline</Badge>
        );
      },
    },
    ...(isAdmin
      ? [
          {
            key: "actions",
            header: "",
            render: (row: WorkerToken) => (
              <Table.Actions>
                {row.active ? (
                  <Table.Action variant="danger" onClick={() => confirmRevoke(row)}>
                    Revoke
                  </Table.Action>
                ) : row.deleted ? null : (
                  <Table.Action variant="danger" onClick={() => confirmDelete(row)}>
                    Delete
                  </Table.Action>
                )}
              </Table.Actions>
            ),
          } satisfies Column<WorkerToken>,
        ]
      : []),
  ];

  return (
    <>
      <Page>
        <Page.Header
          title="Workers"
          subtitle="delivery processes and their tokens"
          subtitleVariant="tagline"
          actions={
            <>
              <Switch
                label={showDeleted ? `Deleted shown (${deletedCount})` : "Show deleted"}
                checked={showDeleted}
                onChange={(e) => setShowDeleted(e.target.checked)}
              />
              <RefreshButton queryKeys={REFRESH_KEYS} live />
              {isAdmin && (
                <Button variant="primary" onClick={() => setModal({ kind: "create" })}>
                  <Plus size={16} aria-hidden="true" />
                  Create token
                </Button>
              )}
            </>
          }
        />
        <Page.Content stack gap="lg">
          <Page.SectionHeader title="Instances" />
          <Table<WorkerInstance>
            striped
            cards
            columns={instanceColumns}
            rows={instances.data ?? []}
            rowKey={(r) => r.id}
            isLoading={instances.isLoading}
            error={instances.error}
            onRetry={instances.refetch}
            empty={
              <EmptyState
                title="No worker is connected"
                description="A worker appears here once it polls with one of the tokens below. Workers that shut down cleanly leave the list; one that vanishes stays listed as offline for 24 hours."
              />
            }
          />

          <Page.SectionHeader title="Tokens" />
          <Table<WorkerToken>
            striped
            cards
            columns={columns}
            rows={data ?? []}
            rowKey={(r) => r.id}
            isLoading={isLoading}
            error={error}
            onRetry={refetch}
            empty={
              <EmptyState
                title="No worker tokens"
                description="Without a connected worker, messages are not sent."
              />
            }
          />
        </Page.Content>
      </Page>

      {modal.kind === "create" && (
        <CreateWorkerModal
          onClose={() => setModal({ kind: "closed" })}
          onCreated={(token, name, sms) =>
            setModal({ kind: "reveal", token, name, sms })
          }
        />
      )}

      <TokenRevealModal
        open={modal.kind === "reveal"}
        title="Worker token created"
        noun="token"
        token={modal.kind === "reveal" ? modal.token : ""}
        // Only an SMS worker has a phone to provision; an e-mail worker is a
        // process on a server and has nothing to point a camera at.
        provision={
          modal.kind === "reveal" && modal.sms
            ? { name: modal.name }
            : undefined
        }
        onClose={() => setModal({ kind: "closed" })}
      />
    </>
  );
}
